import { Location } from '@angular/common';
import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { CustomTableSelectComponent } from 'src/app/components/custom-table-select/custom-table-select.component';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { TaxModel } from 'src/app/models/management/tax.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { CityTableModel } from 'src/app/models/tables/management/city-table.model';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { CityService } from 'src/app/services/management/city.service';
import { TaxService } from 'src/app/services/management/tax.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-tax',
  templateUrl: './register-tax.component.html',
  styleUrls: ['./register-tax.component.scss'],
  standalone: false
})
export class RegisterTaxComponent implements OnInit {

  @ViewChild('selectCity') citySelectionModalComponent?: CustomModalComponent | null = null;
  ascendantCity: boolean = true;
  branchId: number = 0;
  city: string = '';
  cityData: PagedResponseModel<CityTableModel> = new PagedResponseModel<CityTableModel>();
  columnSortCity: string | null = null;
  componentCitySelection = CustomTableSelectComponent;
  department: string = '';
  disabledForm: boolean = false;
  filtersCity: ColumnResponseModel[] = [];
  formTax: FormGroup = new FormGroup({});
  headersCity: ColumnResponseModel[] = [];
  inputsForSelectCityModal$ = new BehaviorSubject<any>({
    headers: [],
    data: [],
    filters: [],
    showCheckbox: true,
    showRecover: true
  });
  isOpened: string | undefined = undefined;
  limitCity: number = 10;
  listTypes: any[] = [];
  nameWindow: string = 'Captura Impuestos';
  output$ = new Subject<any>();
  pageCity: number = 1;
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  subscription: Subscription;
  taxContract: TaxModel = new TaxModel();
  taxId: number = 0;
  url: string = 'tax';

  constructor(private confirmDialogService: ConfirmDialogService,
    private activatedRoute: ActivatedRoute,
    private cityService: CityService,
    private formBuilder: FormBuilder,
    private headerService: HeaderService,
    private location: Location,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private taxService: TaxService,
    private windowService: WindowService) {
    this.validateForm(new TaxModel());
  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.activatedRoute.params.subscribe(params => {
      this.taxId = Object.keys(params).length > 0 ? params?.id : null;
    });
    await this.getPermission();
    this.handleInitialState();
    this.subscription = this.output$.subscribe(({ type, payload }) => {
      switch (type) {
        case 'onPageChange':
          this.onPageChangeCity(payload);
          break;
        case 'onFilterChange':
          this.onFilterChangeCity(payload);
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  changeTax(code: string): void {
    const trimmedCode = code?.trim();
    const currentCode = this.taxContract?.code;
    const taxTypeId = this.formTax.get('taxTypeId')?.value;
    if (!taxTypeId) {
      this.toastHandlerService.showToast('warning', 'Debe seleccionar el Tipo de Impuesto primero.');
      return;
    }
    if (!trimmedCode) {
      this.createNewRegister();
      return;
    }
    if (currentCode !== trimmedCode) {
      this.getTaxByCode(taxTypeId, trimmedCode);
    }
  }

  async changeType(typeId: number) {
    this.city = '';
    this.department = '';
    this.taxContract = new TaxModel();
    this.taxContract.taxTypeId = typeId;
    this.validateForm(this.taxContract);
    this.location.replaceState(`/${this.url}/register`);
  }

  createNewRegister() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('warning', `El usuario no tiene permisos para crear impuestos.
        Comuníquese con el administrador del sistema.`);
      return;
    }
    this.city = '';
    this.department = '';
    this.taxId = 0;
    this.taxContract = new TaxModel();
    this.taxContract.taxTypeId = this.formTax.get("taxTypeId")?.value;
    this.validateForm(this.taxContract);
    this.location.replaceState(`/${this.url}/register`);
  }

  async delete() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('error', `El usuario no tiene permisos para eliminar impuestos.
        Comuníquese con el administrador del sistema.`);
      return;
    }
    if (!this.taxId) {
      this.toastHandlerService.showToast('warning', 'Debe crear un impuesto.');
      return;
    }
    if (await this.confirmDialogService.confirm('¿Realmente desea eliminar el impuesto?')) {
      try {
        await firstValueFrom(this.taxService.deleteTax(this.taxContract.taxId));
        this.createNewRegister();
        this.toastHandlerService.showToast('success', 'El impuesto fue eliminado exitosamente.');
      } catch (error) {
        console.error('Error al eliminar el impuesto.', error);
      }
    }
  }

  async getAllTypesForSelect() {
    try {
      this.listTypes = await firstValueFrom(this.taxService.getAllTypesForSelect());
    } catch (error) {
      console.error('Error al cargar los tipos de impuestos.', error);
    }
  }

  async getCityDepartment(cityId: number) {
    try {
      const response: any = await firstValueFrom(this.cityService.getCityDepartment(cityId));
      this.city = response.city;
      this.department = response.department;
    } catch (error) {
      console.error('Error al obtener la ciudad y el departamento.', error);
    }
  }

  async getCitiesForSelection() {
    try {
      this.cityData = await firstValueFrom(this.cityService.getCitiesForSelection(this.pageCity, this.limitCity,
        this.columnSortCity, this.ascendantCity, this.filtersCity));
      this.updateInputsForSelectCityModal();
    } catch (error) {
      console.error('Error al obtener las ciudades para el modal.', error);
    }
  }

  async getColumnsForCitiesSelection() {
    try {
      if (this.headersCity.length === 0) {
        this.headersCity = await firstValueFrom(this.cityService.getColumnsForCitySelection());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para el modal de ciudades.', error);
    }
  }

  async getTaxByCode(taxTypeId: number, code: string) {
    if (code == this.taxContract.code) { return; }
    try {
      const response = await firstValueFrom(this.taxService.getTaxByCode(taxTypeId, code));
      this.taxId = response.taxId;
      this.taxContract = response;
      this.validateForm(this.taxContract);
      if (response.cityId) {
        await this.getCityDepartment(response.cityId);
      } else {
        this.city = '';
        this.department = '';
      }
    } catch (error) {
      console.error('Error al obtener el impuesto por codigo', error);
    }
  }

  async getTaxById() {
    try {
      const response = await firstValueFrom(this.taxService.getTaxById(this.taxId));
      this.taxContract = response;
      this.validateForm(this.taxContract);
      if (response.cityId) { await this.getCityDepartment(response.cityId); }
      this.location.replaceState(`/${this.url}/register/${this.taxId}`);
    } catch (error) {
      console.error('Error al obtener el impuesto por id.', error);
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast('error', `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`);
      }
    } else {
      localStorage.clear();
    }
    await Promise.all([
      this.getAllTypesForSelect(),
      this.getPermissionProfileByUrl(),
    ]);
  }

  async getPermissionProfileByUrl() {
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  async handleInitialState() {
    if (this.taxId !== null && this.taxId !== 0) {
      await this.getTaxById();
    } else {
      this.createNewRegister();
    }
  }

  async onFilterChangeCity(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filtersCity = this.removeDuplicates(filters);
    this.filtersCity.forEach((filter: any) => this.processFilter(filter));
    this.updateInputsForSelectCityModal();
  }

  async onPageChangeCity(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.pageCity = event.page;
    this.limitCity = event.pageSize;
    this.columnSortCity = event.sortField || null;
    this.ascendantCity = event.sortOrder === 1;
    this.getCitiesForSelection();
  }

  async openCitySelectionModal() {
    try {
      this.filtersCity = [];
      this.updateInputsForSelectCityModal();
      await Promise.all([
        this.getColumnsForCitiesSelection(),
        this.getCitiesForSelection()
      ]);
      this.citySelectionModalComponent?.show();
    } catch (error) {
      console.error('Error al abrir el modal de selección de ciudad.', error);
    }
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filtersCity = this.filtersCity.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filtersCity.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  async setCityDepartment(selectedItem: any) {
    const [city] = selectedItem;
    this.city = `${city.code} - ${city.name}`;
    this.department = `${city.departmentCode} - ${city.departmentName}`;
    this.formTax.patchValue({ cityId: city.id, departmentId: city.departmentId });
  }

  async send() {
    this.validateFormControls();
    if (this.formTax.valid) {
      const modelTax = this.formTax.getRawValue() as TaxModel;
      if (this.taxId == 0) {
        try {
          const response = await firstValueFrom(this.taxService.createTax(modelTax));
          this.taxId = response.taxId;
          this.taxContract = response;
          this.validateForm(this.taxContract);
          this.location.replaceState(`/${this.url}/register/${this.taxId}`);
          this.toastHandlerService.showToast('success', `El impuesto fue creado exitosamente.`);
        } catch (error) {
          console.error('Error al crear el impuesto.', error);
        }
      } else {
        this.taxContract.alterCode = modelTax.alterCode;
        this.taxContract.alterCode2 = modelTax.alterCode2;
        this.taxContract.name = modelTax.name;
        this.taxContract.rate = modelTax.rate;
        this.taxContract.minimumBase = modelTax.minimumBase;
        this.taxContract.saleAccount = modelTax.saleAccount;
        this.taxContract.saleDevolutionAccount = modelTax.saleDevolutionAccount;
        this.taxContract.purchaseAccount = modelTax.purchaseAccount;
        this.taxContract.purchaseDevolutionAccount = modelTax.purchaseDevolutionAccount;
        this.taxContract.departmentId = modelTax.departmentId;
        this.taxContract.cityId = modelTax.cityId;
        this.taxContract.payAccountSelfWithholdingFuente = modelTax.payAccountSelfWithholdingFuente;
        this.taxContract.devolutionAccountSelfWithholdingFuente = modelTax.devolutionAccountSelfWithholdingFuente;
        try {
          const response = await firstValueFrom(this.taxService.updateTax(this.taxContract));
          this.taxContract = response;
          this.validateForm(this.taxContract);
          this.toastHandlerService.showToast('success', `El impuesto fue modificado exitosamente.`);
        } catch (error) {
          console.error('Error al actualizar el impuesto.', error);
        }
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      taxTypeId: {
        required: 'Debe seleccionar un Tipo de Impuesto.',
        min: 'Debe seleccionar un Tipo de Impuesto válido.'
      },
      code: {
        required: 'El campo Código es obligatorio.',
        maxlength: 'El Código no puede tener más de 6 caracteres.'
      },
      alterCode: {
        maxlength: 'El Código Alternativo 1 no puede tener más de 10 caracteres.'
      },
      alterCode2: {
        maxlength: 'El Código Alternativo 2 no puede tener más de 10 caracteres.'
      },
      name: {
        required: 'El campo Nombre es obligatorio.',
        maxlength: 'El Nombre no puede tener más de 70 caracteres.'
      },
      rate: {
        min: 'La Tasa no puede ser menor que 0.',
        max: 'La Tasa no puede superar 999.99.'
      },
      minimumBase: {
        min: 'La Base Mínima no puede ser menor que 0.',
        max: 'La Base Mínima no puede superar 99.999.999.999.99.'
      },
      saleAccount: {
        maxlength: 'La Cuenta de Venta no puede tener más de 10 caracteres.'
      },
      saleDevolutionAccount: {
        maxlength: 'La Cuenta de Devolución de Venta no puede tener más de 10 caracteres.'
      },
      purchaseAccount: {
        maxlength: 'La Cuenta de Compra no puede tener más de 10 caracteres.'
      },
      purchaseDevolutionAccount: {
        maxlength: 'La Cuenta de Devolución de Compra no puede tener más de 10 caracteres.'
      },
      departmentId: {
        required: 'Debe seleccionar un Departamento.'
      },
      cityId: {
        required: 'Debe seleccionar una Ciudad.',
        min: 'Debe seleccionar una Ciudad válida.'
      },
      payAccountSelfWithholdingFuente: {
        maxlength: 'La Cuenta de Retención en la Fuente no puede tener más de 10 caracteres.'
      },
      devolutionAccountSelfWithholdingFuente: {
        maxlength: 'La Cuenta de Devolución de Retención en la Fuente no puede tener más de 10 caracteres.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formTax.get(controlName);
      if (control && control.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  updateInputsForSelectCityModal() {
    this.inputsForSelectCityModal$.next({
      headers: this.headersCity,
      data: this.cityData,
      filters: this.filtersCity,
      showCheckbox: true,
      showRecover: true
    });
  }

  updateFormPermision() {
    if (this.permissionUpdatable) {
      this.disabledForm = false;
      setTimeout(() => this.formTax.enable());
    } else {
      this.disabledForm = true;
      setTimeout(() => this.formTax.disable());
    }
  }

  validateForm(tax: TaxModel) {
    this.formTax = this.formBuilder.group({
      taxTypeId: [tax.taxTypeId, [Validators.required, Validators.min(1)]],
      code: [tax.code, [Validators.required, Validators.maxLength(6)]],
      alterCode: [tax.alterCode, [Validators.maxLength(10)]],
      alterCode2: [tax.alterCode2, [Validators.maxLength(10)]],
      name: [tax.name, [Validators.required, Validators.maxLength(70)]],
      rate: [tax.rate, [Validators.min(0), Validators.max(999.99)]],
      minimumBase: [tax.minimumBase, [Validators.min(0), Validators.max(99999999999.99)]],
      saleAccount: [tax.saleAccount, [Validators.maxLength(10)]],
      saleDevolutionAccount: [tax.saleDevolutionAccount, [Validators.maxLength(10)]],
      purchaseAccount: [tax.purchaseAccount, [Validators.maxLength(10)]],
      purchaseDevolutionAccount: [tax.purchaseDevolutionAccount, [Validators.maxLength(10)]],
      payAccountSelfWithholdingFuente: [tax.payAccountSelfWithholdingFuente, [Validators.maxLength(10)]],
      devolutionAccountSelfWithholdingFuente: [tax.devolutionAccountSelfWithholdingFuente, [Validators.maxLength(10)]],
      departmentId: [tax.departmentId],
      cityId: [tax.cityId],
    });
    this.updateFormPermision();
  }

  validateFormControls() {
    Object.keys(this.formTax.controls).forEach((key) => {
      const control = this.formTax.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
